/**
 * Good Atoms Daily "Undring" — Wonder of the Day
 *
 * 21 rotating wonder prompts (3 weeks), one per day via dayOfYear % length.
 * Each is voiced by a specific Good Atoms mascot and linked to a related lesson.
 */

import type { MascotSubject } from '../components/mascots'

export interface DailyWonderDef {
  mascot:     MascotSubject
  questionNo: string   // Norwegian wonder prompt
  questionEn: string   // English wonder prompt
  hintNo:     string   // Short follow-up / teaser
  hintEn:     string
  lessonId?:  string   // Optional link to a lesson
}

export const DAILY_WONDERS: DailyWonderDef[] = [
  // ── Mathematics (Hex) ────────────────────────────────────────────────────
  {
    mascot:     'mathematics',
    questionNo: 'Kan du telle til uendelig?',
    questionEn: 'Can you count to infinity?',
    hintNo:     'Matematikere har funnet opp tall som er uendelig store — og noen er større enn andre!',
    hintEn:     'Mathematicians invented infinities — and some infinities are larger than others!',
    lessonId:   'math-fractions-1',
  },
  {
    mascot:     'mathematics',
    questionNo: 'Hvorfor er det ingen retteste linje mellom Oslo og New York på et vanlig kart?',
    questionEn: 'Why is the shortest path between Oslo and New York curved on a map?',
    hintNo:     'Jorden er rund, og den korteste veien mellom to punkter på en kule er faktisk en bue!',
    hintEn:     'The Earth is round, so the shortest path between two points on a sphere is an arc!',
    lessonId:   'math-geometry-1',
  },
  {
    mascot:     'mathematics',
    questionNo: 'Hva har pizza, musikk og kuttede pizzastykker til felles?',
    questionEn: 'What do pizza, music and pizza slices have in common?',
    hintNo:     'Brøk! Pizzastykker er brøkdeler, og noter i musikken er brøkdeler av en takt.',
    hintEn:     'Fractions! Pizza slices are fractions, and music notes are fractions of a beat.',
    lessonId:   'math-fractions-1',
  },

  // ── Science (Blobby) ─────────────────────────────────────────────────────
  {
    mascot:     'science',
    questionNo: 'Hvorfor skifter blader farge om høsten?',
    questionEn: 'Why do leaves change colour in autumn?',
    hintNo:     'Det grønne klorofyllet forsvinner — og avslører de skjulte gule og røde fargene som var der hele tiden!',
    hintEn:     'The green chlorophyll disappears — revealing hidden yellow and red pigments that were there all along!',
    lessonId:   'science-evolution-1',
  },
  {
    mascot:     'science',
    questionNo: 'Er en flamingo rosa fordi den spiser reker?',
    questionEn: 'Is a flamingo pink because it eats shrimp?',
    hintNo:     'Ja! Rekene og algene flamingoen spiser inneholder et fargestoff som gjør dem rosa. Uten det ville de vært hvite.',
    hintEn:     "Yes! The shrimp and algae flamingos eat contain a pigment that makes them pink. Without it they'd be white.",
    lessonId:   'science-evolution-1',
  },
  {
    mascot:     'science',
    questionNo: 'Hvor mange celler er det i menneskekroppen?',
    questionEn: 'How many cells are in the human body?',
    hintNo:     'Omtrent 37 billioner! Og de fleste er ikke en gang menneskeceller — men bakterier.',
    hintEn:     "About 37 trillion! And most of them aren't even human cells — they're bacteria.",
  },
  {
    mascot:     'science',
    questionNo: 'Hva er det tyngste elementet i universet?',
    questionEn: 'What is the heaviest element in the universe?',
    hintNo:     'De tyngste grunnstoffene ble skapt i kollisjoner mellom nøytronstjerner — inkludert gullet i smykker!',
    hintEn:     'The heaviest elements were forged in neutron star collisions — including the gold in jewellery!',
  },

  // ── Technology (Pixel) ───────────────────────────────────────────────────
  {
    mascot:     'technology',
    questionNo: 'Hva er egentlig internett laget av?',
    questionEn: 'What is the internet actually made of?',
    hintNo:     'Mesteparten av internett reiser gjennom tynne glassfibre under verdenshavene. Det er mer kabel enn du tror!',
    hintEn:     'Most of the internet travels through thin glass fibres under the oceans. Far more cable than you think!',
    lessonId:   'tech-internet-1',
  },
  {
    mascot:     'technology',
    questionNo: 'Kan en datamaskin forstå "2 + 2 = 4" uten å bruke tall?',
    questionEn: 'Can a computer understand "2 + 2 = 4" without using numbers?',
    hintNo:     'Ja! Datamaskiner bruker bare 0-er og 1-er — binærtall — og likevel kan de spille av filmer og fly fly!',
    hintEn:     'Yes! Computers only use 0s and 1s — binary — and yet they can play movies and fly aircraft!',
    lessonId:   'tech-internet-1',
  },
  {
    mascot:     'technology',
    questionNo: 'Hva skjer med en melding du sender, fra du trykker send til vennen din leser den?',
    questionEn: 'What happens to a message you send, from the moment you press send to your friend reading it?',
    hintNo:     'Den deles opp i tusenvis av biter, sendes via forskjellige ruter rundt verden, og settes sammen igjen!',
    hintEn:     "It's split into thousands of packets, sent via different routes around the world, and reassembled!",
    lessonId:   'tech-internet-1',
  },

  // ── Engineering (Cogs) ───────────────────────────────────────────────────
  {
    mascot:     'engineering',
    questionNo: 'Hva holder Eiffeltårnet oppe?',
    questionEn: 'What keeps the Eiffel Tower standing?',
    hintNo:     'Triangler! Ingeniører bruker dem overalt fordi de er den eneste formen som ikke kan bøye seg under press.',
    hintEn:     "Triangles! Engineers use them everywhere because they're the only shape that can't distort under pressure.",
    lessonId:   'eng-structures-1',
  },
  {
    mascot:     'engineering',
    questionNo: 'Hvorfor bøyes jernbaneskinner i varmt vær?',
    questionEn: 'Why do railway tracks buckle in hot weather?',
    hintNo:     'Metall utvider seg når det varmes opp. Ingeniører må planlegge for dette — og legger inn "pusterom" i skinnene!',
    hintEn:     'Metal expands when heated. Engineers must plan for this — they build tiny gaps into tracks!',
    lessonId:   'eng-structures-1',
  },
  {
    mascot:     'engineering',
    questionNo: 'Kan du bygge en bro med bare papir som bærer en bok?',
    questionEn: 'Can you build a bridge from just paper that holds a book?',
    hintNo:     'Ja! Legg papiret i bølger eller folder — bølgeformen gir det styrke, akkurat som bølgepapp.',
    hintEn:     'Yes! Fold the paper into corrugations — the wave shape gives it strength, just like corrugated cardboard.',
    lessonId:   'eng-structures-1',
  },

  // ── Arts (Blossom) ───────────────────────────────────────────────────────
  {
    mascot:     'arts',
    questionNo: 'Hvorfor ser vi ansikter i skyene, trær og brødristede brødskiver?',
    questionEn: 'Why do we see faces in clouds, trees and toast?',
    hintNo:     'Hjernen vår er trent til å finne ansikter overalt — det er en superevne som holdt forfedre våre trygge!',
    hintEn:     "Our brain is trained to spot faces everywhere — it's a superpower that kept our ancestors safe!",
    lessonId:   'arts-composition-1',
  },
  {
    mascot:     'arts',
    questionNo: 'Hva gjør et fotografi minneverdig?',
    questionEn: 'What makes a photograph memorable?',
    hintNo:     'Det er sjelden motivet — det er komposisjonen. Tredjedelsregelen brukes av de beste fotografene i verden.',
    hintEn:     "It's rarely the subject — it's the composition. The rule of thirds is used by the world's best photographers.",
    lessonId:   'arts-composition-1',
  },
  {
    mascot:     'arts',
    questionNo: 'Kan musikk endre humøret ditt på under 1 sekund?',
    questionEn: 'Can music change your mood in under 1 second?',
    hintNo:     'Ja! Hjernen din gjenkjenner dur og moll (glad og trist lyd) nesten øyeblikkelig — raskere enn et øyeblikk!',
    hintEn:     'Yes! Your brain recognises major and minor (happy and sad sounds) almost instantly — faster than a blink!',
  },

  // ── Nature (Sprout) ──────────────────────────────────────────────────────
  {
    mascot:     'nature',
    questionNo: 'Hva er den eldste levende tingen på Jorden?',
    questionEn: 'What is the oldest living thing on Earth?',
    hintNo:     'En gran i Sverige er over 9500 år gammel! Den har overlevd istider, vikinger og hele menneskenes sivilisasjon.',
    hintEn:     'A spruce tree in Sweden is over 9,500 years old! It survived ice ages, Vikings, and all of human civilisation.',
  },
  {
    mascot:     'nature',
    questionNo: 'Snakker trær med hverandre?',
    questionEn: 'Do trees talk to each other?',
    hintNo:     'Ja! Gjennom "Wood Wide Web" — et nettverk av sopptrakter under bakken — deler de næringsstoffer og advarsler.',
    hintEn:     'Yes! Through the "Wood Wide Web" — a network of fungal threads underground — they share nutrients and warnings.',
    lessonId:   'science-evolution-1',
  },
  {
    mascot:     'nature',
    questionNo: 'Hvorfor er havet blått, men vannet i glasset ditt klart?',
    questionEn: 'Why is the ocean blue but the water in your glass clear?',
    hintNo:     'Vannet absorberer rødt lys og reflekterer blått — men du trenger mye av det før fargen vises!',
    hintEn:     'Water absorbs red light and reflects blue — but you need a lot of it before the colour shows!',
  },

  // ── Future Skills (Nova) ─────────────────────────────────────────────────
  {
    mascot:     'futureskills',
    questionNo: 'Hva er den viktigste ferdigheten du kan lære for fremtiden?',
    questionEn: 'What is the most important skill you can learn for the future?',
    hintNo:     'Å lære å lære! Verden forandrer seg så raskt at evnen til å suge til seg ny kunnskap er viktigere enn noen gang.',
    hintEn:     'Learning how to learn! The world changes so fast that the ability to absorb new knowledge matters most.',
  },
  {
    mascot:     'futureskills',
    questionNo: 'Kan en robot noen gang bli kreativ?',
    questionEn: 'Can a robot ever be truly creative?',
    hintNo:     'AI kan lage kunst og musikk — men det trenger alltid menneskelig nysgjerrighet for å stille de beste spørsmålene.',
    hintEn:     'AI can make art and music — but it always needs human curiosity to ask the best questions.',
  },
]

// ─── Pick today's wonder ──────────────────────────────────────────────────────

/** Returns the wonder for today (changes at midnight, same for all users). */
export function getTodaysWonder(): DailyWonderDef {
  const now  = new Date()
  const start = new Date(now.getFullYear(), 0, 0)
  const diff  = now.getTime() - start.getTime()
  const dayOfYear = Math.floor(diff / 86_400_000)
  return DAILY_WONDERS[dayOfYear % DAILY_WONDERS.length]
}
